import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { PageLoadingFallback } from "@/components/PageLoadingFallback";

// Context
import { AppProvider } from "@/context/AppContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { PaymentGate } from "@/components/auth/PaymentGate";
import { OnboardingGate } from "@/components/auth/OnboardingGate";
import { RoleRoute } from "@/components/auth/RoleRoute";
import { SessionExpiredListener } from "@/components/auth/SessionExpiredListener";
import { PwaRuntimeBridge } from "@/components/pwa/PwaRuntimeBridge";
import { ChatbotWidget } from "@/components/ChatbotWidget";
import { DocumentTitle } from "@/components/DocumentTitle";
import { PackageGate } from "@/components/auth/PackageGate";
import { PreAssessmentRoute } from "@/components/auth/PreAssessmentRoute";
import { PreAssessmentRedirect } from "@/components/auth/PreAssessmentRedirect";

// Pages (lazy-loaded for smaller initial bundle)
const Registration = lazy(() => import("@/pages/Registration"));
const Login = lazy(() => import("@/pages/Login"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PemohonDashboard = lazy(() =>
  import("@/pages/dashboard/PemohonDashboard").then((m) => ({ default: m.PemohonDashboard })),
);
const SLHSDashboard = lazy(() =>
  import("@/pages/dashboard/SLHSDashboard").then((m) => ({ default: m.SLHSDashboard })),
);
const HACCPDashboard = lazy(() =>
  import("@/pages/dashboard/HACCPDashboard").then((m) => ({ default: m.HACCPDashboard })),
);
const GapAssessment = lazy(() => import("@/pages/GapAssessment"));
const AuditExternal = lazy(() => import("@/pages/AuditExternal"));
const Documents = lazy(() => import("@/pages/Documents"));
const ManualSjph = lazy(() => import("@/pages/ManualSjph"));
const Certificate = lazy(() => import("@/pages/Certificate"));
const CertificateVerifyPublic = lazy(() => import("@/pages/CertificateVerifyPublic"));
const AccountSettingsLayout = lazy(() => import("@/pages/settings/AccountSettingsLayout"));
const SettingsProfilePage = lazy(() => import("@/pages/settings/SettingsProfilePage"));
const SettingsBusinessPage = lazy(() => import("@/pages/settings/SettingsBusinessPage"));
const SettingsCompanySectionPage = lazy(() => import("@/pages/settings/SettingsCompanySectionPage"));
const SettingsClientPage = lazy(() => import("@/pages/settings/SettingsClientPage"));
const SettingsSecurityPage = lazy(() => import("@/pages/settings/SettingsSecurityPage"));
const Payment = lazy(() => import("@/pages/Payment"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));

const TADashboard = lazy(() => import("@/pages/ta/TADashboard"));
const TASchedule = lazy(() => import("@/pages/ta/TASchedule"));
const TAClients = lazy(() => import("@/pages/ta/TAClients"));
const TAClientDocumentsRedirect = lazy(() => import("@/pages/ta/TAClientDocumentsRedirect"));
const TAClientAssignments = lazy(() => import("@/pages/ta/TAClientAssignments"));
const TATeamDirectory = lazy(() => import("@/pages/ta/TATeamDirectory"));
const TADocumentUpload = lazy(() => import("@/pages/ta/TADocumentUpload"));
const TATasks = lazy(() => import("@/pages/ta/TATasks"));
const TAMyTasks = lazy(() => import("@/pages/ta/TAMyTasks"));
const TAAttendance = lazy(() => import("@/pages/ta/TAAttendance"));
const TAKPI = lazy(() => import("@/pages/ta/TAKPI"));
const SuperadminDashboard = lazy(() => import("@/pages/superadmin/SuperadminDashboard"));
const ClientManagement = lazy(() => import("@/pages/superadmin/ClientManagement"));
const TaClientAssignments = lazy(() => import("@/pages/superadmin/TaClientAssignments"));
const TaDirectory = lazy(() => import("@/pages/superadmin/TaDirectory"));
const AuditorClientAssignments = lazy(() => import("@/pages/superadmin/AuditorClientAssignments"));
const SuperadminClientDetail = lazy(() => import("@/pages/superadmin/SuperadminClientDetail"));
const UserManagement = lazy(() => import("@/pages/superadmin/UserManagement"));
const SuperadminUserDetail = lazy(() => import("@/pages/superadmin/SuperadminUserDetail"));
const SuperadminPemohonSettingsLayout = lazy(() =>
  import("@/pages/superadmin/SuperadminPemohonSettingsLayout"),
);
const AdminPemohonProfilePage = lazy(() =>
  import("@/pages/superadmin/pemohon-settings/AdminPemohonProfilePage"),
);
const AdminPemohonBusinessPage = lazy(() =>
  import("@/pages/superadmin/pemohon-settings/AdminPemohonBusinessPage"),
);
const AdminPemohonCompanySectionPage = lazy(() =>
  import("@/pages/superadmin/pemohon-settings/AdminPemohonCompanySectionPage"),
);
const AdminPemohonClientPage = lazy(() =>
  import("@/pages/superadmin/pemohon-settings/AdminPemohonClientPage"),
);
const SuperadminSettings = lazy(() => import("@/pages/superadmin/SuperadminSettings"));
const SuperadminActivityPage = lazy(() => import("@/pages/superadmin/SuperadminActivity"));
const SuperadminPayments = lazy(() => import("@/pages/superadmin/SuperadminPayments"));
const SuperadminSales = lazy(() => import("@/pages/superadmin/SuperadminSales"));
const SuperadminVouchers = lazy(() => import("@/pages/superadmin/SuperadminVouchers"));
const SuperadminReferrals = lazy(() => import("@/pages/superadmin/SuperadminReferrals"));

const NotFound = lazy(() => import("@/pages/NotFound"));

function SuperadminGate({ children }) {
  return (
    <ProtectedRoute>
      <RoleRoute allowedRoles={["SUPER_ADMIN"]}>{children}</RoleRoute>
    </ProtectedRoute>
  );
}

function PemohonGate({ children }) {
  return (
    <ProtectedRoute>
      <RoleRoute allowedRoles={["PEMOHON"]}>{children}</RoleRoute>
    </ProtectedRoute>
  );
}

function TaGate({ children }) {
  return (
    <ProtectedRoute>
      <RoleRoute allowedRoles={["TECHNICAL_ASSISTANT"]}>{children}</RoleRoute>
    </ProtectedRoute>
  );
}

function AuditorGate({ children }) {
  return (
    <ProtectedRoute>
      <RoleRoute allowedRoles={["AUDITOR"]}>{children}</RoleRoute>
    </ProtectedRoute>
  );
}

function PemohonPaidShell({ children }) {
  return (
    <ProtectedRoute>
      <OnboardingGate>
        <PaymentGate>{children}</PaymentGate>
      </OnboardingGate>
    </ProtectedRoute>
  );
}

function PemohonPaidGate({ children }) {
  return (
    <PemohonGate>
      <OnboardingGate>
        <PaymentGate>{children}</PaymentGate>
      </OnboardingGate>
    </PemohonGate>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <TooltipProvider delayDuration={250} skipDelayDuration={100}>
        <DocumentTitle />
        <ChatbotWidget />
        <PwaRuntimeBridge />
        <SessionExpiredListener />
        <Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/register" element={<Registration />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify/certificate/:token" element={<CertificateVerifyPublic />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <OnboardingGate>
                    <Payment />
                  </OnboardingGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PemohonPaidShell>
                  <Dashboard />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/dashboard/halal"
              element={
                <PemohonPaidShell>
                  <PackageGate code="HALAL">
                    <PemohonDashboard certificationFlow="HALAL" />
                  </PackageGate>
                </PemohonPaidShell>
              }
            />
            <Route
              path="/dashboard/slhs"
              element={
                <PemohonPaidShell>
                  <PackageGate code="SLHS">
                    <SLHSDashboard />
                  </PackageGate>
                </PemohonPaidShell>
              }
            />
            <Route
              path="/dashboard/haccp"
              element={
                <PemohonPaidShell>
                  <PackageGate code="HACCP">
                    <HACCPDashboard />
                  </PackageGate>
                </PemohonPaidShell>
              }
            />
            <Route
              path="/pre-assessment"
              element={
                <PemohonPaidShell>
                  <PreAssessmentRedirect />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/pre-assessment/:certSlug"
              element={
                <PemohonPaidShell>
                  <PreAssessmentRoute />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/gap-assessment/*"
              element={
                <PemohonPaidShell>
                  <GapAssessment />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/audit-external/*"
              element={
                <PemohonPaidShell>
                  <AuditExternal />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/settings"
              element={
                <PemohonPaidShell>
                  <AccountSettingsLayout />
                </PemohonPaidShell>
              }
            >
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<SettingsProfilePage />} />
              <Route path="business" element={<SettingsBusinessPage />} />
              <Route path="company/:section" element={<SettingsCompanySectionPage />} />
              <Route path="registration" element={<Navigate to="/settings/company/pelaku" replace />} />
              <Route path="client" element={<SettingsClientPage />} />
              <Route path="security" element={<SettingsSecurityPage />} />
            </Route>
            <Route
              path="/documents"
              element={
                <PemohonPaidShell>
                  <Documents />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/certificate"
              element={
                <PemohonPaidShell>
                  <Certificate />
                </PemohonPaidShell>
              }
            />
            <Route
              path="/ta"
              element={
                <TaGate>
                  <TADashboard />
                </TaGate>
              }
            />
            <Route
              path="/ta/documents/upload"
              element={
                <TaGate>
                  <TADocumentUpload />
                </TaGate>
              }
            />
            <Route
              path="/ta/documents"
              element={
                <TaGate>
                  <Documents />
                </TaGate>
              }
            />
            <Route
              path="/ta/manual-sjph"
              element={
                <TaGate>
                  <ManualSjph />
                </TaGate>
              }
            />
            <Route
              path="/ta/schedule"
              element={
                <TaGate>
                  <TASchedule />
                </TaGate>
              }
            />
            <Route
              path="/ta/clients/:clientId"
              element={
                <TaGate>
                  <TAClientDocumentsRedirect />
                </TaGate>
              }
            />
            <Route
              path="/ta/clients"
              element={
                <TaGate>
                  <TAClients />
                </TaGate>
              }
            />
            <Route
              path="/ta/client-assignments"
              element={
                <TaGate>
                  <TAClientAssignments />
                </TaGate>
              }
            />
            <Route
              path="/ta/team-directory"
              element={
                <TaGate>
                  <TATeamDirectory />
                </TaGate>
              }
            />
            <Route
              path="/ta/tasks"
              element={
                <TaGate>
                  <TATasks />
                </TaGate>
              }
            />
            <Route
              path="/ta/my-tasks"
              element={
                <TaGate>
                  <TAMyTasks />
                </TaGate>
              }
            />
            <Route
              path="/ta/projects"
              element={
                <TaGate>
                  <Navigate to="/ta/clients" replace />
                </TaGate>
              }
            />
            <Route
              path="/ta/attendance"
              element={
                <TaGate>
                  <TAAttendance />
                </TaGate>
              }
            />
            <Route
              path="/ta/kpi"
              element={
                <TaGate>
                  <TAKPI />
                </TaGate>
              }
            />
            <Route
              path="/auditor"
              element={
                <AuditorGate>
                  <Navigate to="/auditor/documents" replace />
                </AuditorGate>
              }
            />
            <Route
              path="/auditor/documents"
              element={
                <AuditorGate>
                  <Documents />
                </AuditorGate>
              }
            />
            <Route
              path="/superadmin"
              element={
                <SuperadminGate>
                  <SuperadminDashboard />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/clients"
              element={
                <SuperadminGate>
                  <ClientManagement />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/ta-assignments"
              element={
                <SuperadminGate>
                  <TaClientAssignments />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/ta-directory"
              element={
                <SuperadminGate>
                  <TaDirectory />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/auditor-assignments"
              element={
                <SuperadminGate>
                  <AuditorClientAssignments />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/clients/:clientId"
              element={
                <SuperadminGate>
                  <SuperadminClientDetail />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/manual-sjph"
              element={
                <SuperadminGate>
                  <ManualSjph />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/users/:userId/pemohon-settings"
              element={
                <SuperadminGate>
                  <SuperadminPemohonSettingsLayout />
                </SuperadminGate>
              }
            >
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="profile" element={<AdminPemohonProfilePage />} />
              <Route path="business" element={<AdminPemohonBusinessPage />} />
              <Route path="company/:section" element={<AdminPemohonCompanySectionPage />} />
              <Route path="client" element={<AdminPemohonClientPage />} />
            </Route>
            <Route
              path="/superadmin/users/:userId"
              element={
                <SuperadminGate>
                  <SuperadminUserDetail />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/users"
              element={
                <SuperadminGate>
                  <UserManagement />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/activity"
              element={
                <SuperadminGate>
                  <SuperadminActivityPage />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/payments"
              element={
                <SuperadminGate>
                  <SuperadminPayments />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/sales"
              element={
                <SuperadminGate>
                  <SuperadminSales />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/vouchers"
              element={
                <SuperadminGate>
                  <SuperadminVouchers />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/referrals"
              element={
                <SuperadminGate>
                  <SuperadminReferrals />
                </SuperadminGate>
              }
            />
            <Route
              path="/superadmin/settings"
              element={
                <SuperadminGate>
                  <SuperadminSettings />
                </SuperadminGate>
              }
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <Toaster richColors position="top-right" />
        </TooltipProvider>
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
